// Analytics API for tracking frontend events to Langfuse via backend

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface AnalyticsEventMetadata {
  // Page view specific
  source?: string | null;
  campaign?: string | null;
  referrer?: string;
  url_path?: string;

  // Email click specific
  email_recipient?: string;
  email_subject?: string;

  // General
  timestamp_client?: string;
  [key: string]: unknown;
}

interface TrackEventParams {
  eventName: 'page_view' | 'email_button_click';
  sessionId?: string | null;
  chatId?: string | null;
  metadata?: AnalyticsEventMetadata;
}

/**
 * Track an analytics event to Langfuse via backend.
 * Fire-and-forget: errors are logged but don't throw.
 */
async function trackEvent({
  eventName,
  sessionId,
  chatId,
  metadata
}: TrackEventParams): Promise<void> {
  try {
    const enrichedMetadata: AnalyticsEventMetadata = {
      ...metadata,
      timestamp_client: new Date().toISOString(),
      url_path: window.location.pathname,
      referrer: document.referrer || undefined
    };

    const response = await fetch(`${API_BASE_URL}/api/chats/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_name: eventName,
        session_id: sessionId || undefined,
        chat_id: chatId || undefined,
        metadata: enrichedMetadata
      }),
    });

    if (!response.ok) {
      console.warn(`Analytics tracking failed: ${response.status}`);
    }
  } catch (error) {
    // Fire-and-forget: log but don't throw
    console.warn('Analytics tracking error:', error);
  }
}

/**
 * Track page view (typically on QR code scan or direct visit).
 * Call this on app initialization to capture all visitors.
 */
export function trackPageView(params: {
  source?: string | null;
  // True when the source came from this visit's URL (a QR scan or link click),
  // false when it was restored from local storage on a later visit.
  sourceFromUrl?: boolean;
  campaign?: string | null;
  sessionId?: string | null;
}): void {
  trackEvent({
    eventName: 'page_view',
    sessionId: params.sessionId,
    metadata: {
      source: params.source,
      source_from_url: params.sourceFromUrl,
      campaign: params.campaign,
    }
  });
}

/**
 * Track email button click.
 * This event is linked to the chat's trace in Langfuse when chatId is provided.
 */
export function trackEmailClick(params: {
  chatId: string;
  sessionId?: string | null;
  emailRecipient?: string;
  emailSubject?: string;
}): void {
  trackEvent({
    eventName: 'email_button_click',
    sessionId: params.sessionId,
    chatId: params.chatId,
    metadata: {
      email_recipient: params.emailRecipient,
      email_subject: params.emailSubject,
    }
  });
}

/**
 * Track chat started - links page view info (source, campaign) to the chat's trace.
 * Call this when a new chat is created to connect the initial page view to the chat.
 */
export function trackChatStarted(params: {
  chatId: string;
  sessionId?: string | null;
  source?: string | null;
  campaign?: string | null;
}): void {
  trackEvent({
    eventName: 'page_view',
    sessionId: params.sessionId,
    chatId: params.chatId,
    metadata: {
      source: params.source,
      campaign: params.campaign,
      event_context: 'chat_started', // Distinguishes from initial page_view
    }
  });
}
